import * as Sentry from '@sentry/nextjs'
import { DSSpacer, DSText, DSTextAndIcon } from '@zoe/ds-web'
import { Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { Button } from 'react-aria-components'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'
import * as Yup from 'yup'

import { NUTRITION_NEWSLETTER_EMAIL_SUBMITTED } from '@/lib/braze/brazeConstants'
import { registerBrazeUserWithEmail } from '@/lib/braze/brazeUtils'
import { QUIZ_ID_COOKIE_NAME } from '@/lib/checkoutConstants'
import { trackSignupEvent } from '@/lib/gtm/gtmV2'
import type { EmailListType, LinkPosition, Section } from '@/lib/gtm/typesV2'
import { useCheckoutCountry } from '@/lib/hooks/useCheckoutCountry'

const StyledForm = styled(Form)`
  width: 100%;
  padding: 11px 10px 11px 16px;
  border-radius: var(--radius-8);
  border: 1px solid var(--color-border-input);
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledField = styled(Field)`
  background-color: transparent;
  outline: none;
  border: none;
  width: 100%;
  color: var(--color-text-primary-inverse);
  font-family: var(--font-sans-serif-light);
  font-size: var(--font-size-input);
  margin-top: 4px;

  &::placeholder {
    color: var(--color-text-primary-inverse);
  }
`

const StyledButton = styled(Button)`
  border: 0;
  border-radius: var(--radius-8);
  transition: background-color 0.1s ease-in-out;
  background-color: var(--color-surface-neutral);
  color: var(--color-text-button-primary);
  padding: var(--grid-8) var(--grid-16);

  &[data-hovered] {
    cursor: pointer;
  }

  &[data-disabled] {
    opacity: 0.5;
  }
`

const validationSchema = Yup.object({
  email: Yup.string().email('Email address does not look right').required('Please fill in your email'),
})

type FormValues = Yup.InferType<typeof validationSchema> & {
  marketingConsent: boolean
}

const initialValues: FormValues = { email: '', marketingConsent: false }

export const FooterNewsletterSignup = () => {
  const [submitSuccess, setSubmitSuccess] = useState<boolean | undefined>()
  const [cookies] = useCookies([QUIZ_ID_COOKIE_NAME])
  const { isReady, geoLocCountry } = useCheckoutCountry()

  const handleSubmit = async (values: FormValues) => {
    const marketingConsent = true

    const additionalFields = {
      ...(cookies.cUserId && { quizUserId: cookies.cUserId }),
      ...(isReady && geoLocCountry && { country: geoLocCountry }),
    }

    const email = values.email
    const section: Section = 'footer'
    const emailList: EmailListType = 'newsletter'
    const linkPosition: LinkPosition = 'footer'
    if (email) {
      try {
        await registerBrazeUserWithEmail(
          values.email,
          NUTRITION_NEWSLETTER_EMAIL_SUBMITTED,
          marketingConsent,
          Object.keys(additionalFields).length !== 0 ? additionalFields : undefined,
        )
        setSubmitSuccess(true)
        trackSignupEvent({ section, emailList, email, linkPosition })
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            brazeEventName: NUTRITION_NEWSLETTER_EMAIL_SUBMITTED,
            section,
          },
          extra: {
            context: 'Error registering user with Braze',
          },
        })
        console.error('Error registering user with Braze', error)
        setSubmitSuccess(false)
      }
    }
  }

  return (
    <>
      <DSText as="h3" variant="fluid-heading-600" color="primary-inverse">
        Stay up to date with ZOE
      </DSText>
      <DSSpacer direction="vertical" size={16} />

      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange
        validationSchema={validationSchema}
      >
        {({ isSubmitting, isValid, touched }) => (
          <>
            <StyledForm>
              <StyledField id="email" name="email" type="email" placeholder="Enter your email" />
              <>
                <DSSpacer direction="horizontal" size={12} />
                <StyledButton type="submit" isDisabled={!isValid || isSubmitting || !touched}>
                  <DSText as="span" variant="fluid-paragraph-200" weight="semiBold" color="primary">
                    Submit
                  </DSText>
                </StyledButton>
              </>
            </StyledForm>
            <DSSpacer direction="vertical" size={16} />
            {submitSuccess === false && (
              <DSTextAndIcon
                icon={{
                  name: 'close',
                  size: 16,
                  position: 'left',
                  color: 'inverse',
                }}
                color="primary-inverse"
                variant="fluid-paragraph-200"
                weight="regular"
              >
                There has been an error. Please try again.
              </DSTextAndIcon>
            )}
            {submitSuccess && (
              <DSTextAndIcon
                icon={{
                  name: 'tick-in-circle-no-fill',
                  size: 16,
                  position: 'left',
                  color: 'inverse',
                }}
                variant="fluid-paragraph-200"
                weight="regular"
                color="primary-inverse"
              >
                Thank you for signing up!
              </DSTextAndIcon>
            )}
            {submitSuccess === undefined && (
              <DSText as="p" variant="fluid-label-100" weight="light" color="primary-inverse">
                You&apos;ll receive our ongoing science and nutrition emails, plus news and offers.
              </DSText>
            )}
          </>
        )}
      </Formik>
      <DSSpacer direction="vertical" size={32} />
    </>
  )
}
